import { useEffect, useState } from "react";
import { PageSEO } from "@/components/seo/PageSEO";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  created_at: string;
}

export default function Nyheter() {
  const { t } = useTranslation();
  const [news, setNews] = useState<NewsPost[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase
      .from("news_posts")
      .select("id, title, slug, excerpt, published_at, created_at")
      .eq("published", true)
      .order("published_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) setNews(data as unknown as NewsPost[]);
        setLoaded(true);
      });
  }, []);

  return (
    <div>
      <PageSEO title={t("information.news.title")} description={t("seo.nyheter")} />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-heading text-primary">
            {t("information.news.title")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            {t("information.news.subtitle")}
          </p>
        </div>
      </section>

      {/* News list */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          {!loaded ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : news.length === 0 ? (
            <p className="text-muted-foreground">{t("information.news.empty")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link
                    to={`/information/${post.slug}`}
                    className="block bg-card border rounded-xl p-6 hover:shadow-md transition-shadow h-full"
                  >
                    <p className="text-xs text-muted-foreground mb-2">
                      {format(
                        new Date(post.published_at || post.created_at),
                        "d MMMM yyyy",
                        { locale: sv }
                      )}
                    </p>
                    <h3 className="font-semibold text-foreground mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm text-primary mt-3 font-medium">
                      {t("information.readMore")} <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
